import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import IdBadge from "components/IdBadge";
import { useMemo, useState } from "react";
import ColorScaleDetailsDrawer from "./ColorScaleDetailsDrawer";
import { GradientSwatch } from "./ColorScalePicker";
import CreateColorScaleDialog from "./CreateColorScaleDialog";
import { regionKey, useLidarClearance } from "./LidarClearanceContext";
import RegionFilterSelect from "./RegionFilterSelect";
import SortableHeaderCell from "./SortableHeaderCell";
import TableFilterField from "./TableFilterField";
import { buildGradient } from "./colorScaleDisplay";
import type { LidarClearanceColorScale } from "./schemas";
import { createSortComparators } from "./sortComparators";
import t from "./translations";
import { useSortAndFilter } from "./useSortAndFilter";

const COLUMN_COUNT = 3;

interface ColorScaleRow {
  scale: LidarClearanceColorScale;
  gradient: string | undefined;
}

type ColorScaleSortKey = "id" | "stops";

const SORT_COMPARATORS = createSortComparators<ColorScaleRow, ColorScaleSortKey>({
  id: (row) => row.scale.color_scale_id,
  stops: (row) => row.scale.colors.length,
});

function matchesFilter(row: ColorScaleRow, filter: string): boolean {
  return (
    String(row.scale.color_scale_id).includes(filter) ||
    `${row.scale.region}-${row.scale.color_scale_id}`
      .toLowerCase()
      .includes(filter) ||
    String(row.scale.colors.length).includes(filter)
  );
}

export default function ColorScalesTab() {
  const { colorScales, regionTags, loading } = useLidarClearance();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [regionFilter, setRegionFilter] = useState("");

  const selectedScale =
    colorScales.find(
      (scale) => regionKey(scale.region, scale.color_scale_id) === selectedKey
    ) ?? null;

  const regionFilteredColorScales = useMemo(
    () =>
      regionFilter
        ? colorScales.filter((scale) => scale.region === regionFilter)
        : colorScales,
    [colorScales, regionFilter]
  );

  const rows = useMemo(
    () =>
      regionFilteredColorScales.map((scale) => ({
        scale,
        gradient: buildGradient(scale.colors, 1, scale.colors.length - 1),
      })),
    [regionFilteredColorScales]
  );

  const { filterText, setFilterText, sortKey, sortDirection, toggleSort, visibleRows } =
    useSortAndFilter<ColorScaleRow, ColorScaleSortKey>({
      rows,
      matchesFilter,
      comparators: SORT_COMPARATORS,
    });

  return (
    <Box data-testid="color-scales-tab">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <RegionFilterSelect
            regionTags={regionTags}
            value={regionFilter}
            onChange={setRegionFilter}
            testId="color-scales-tab.region-filter"
          />
          <TableFilterField
            value={filterText}
            onChange={setFilterText}
            placeholder={t.FilterByIdPlaceholder}
            testId="color-scales-tab.filter"
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          data-testid="color-scales-tab.new-color-scale"
        >
          {t.NewColorScaleButton}
        </Button>
      </Box>
      <TableContainer component={Paper} data-testid="color-scales-table">
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              <SortableHeaderCell
                sortKeyName="id"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={toggleSort}
                align="center"
                sx={{ width: 90 }}
              >
                {t.IdColumn}
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKeyName="stops"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={toggleSort}
                align="right"
                sx={{ width: 90 }}
              >
                {t.StopsColumn}
              </SortableHeaderCell>
              <TableCell>{t.PreviewColumn}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {colorScales.length === 0 ? "—" : t.NoMatchingRows}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map(({ scale, gradient }) => {
              const key = regionKey(scale.region, scale.color_scale_id);

              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => setSelectedKey(key)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`color-scales-tab.row-${scale.region}-${scale.color_scale_id}`}
                >
                  <TableCell align="center">
                    <IdBadge
                      prefix={scale.region.toUpperCase()}
                      id={scale.color_scale_id}
                    />
                  </TableCell>
                  <TableCell align="right">{scale.colors.length}</TableCell>
                  <TableCell>
                    <GradientSwatch gradient={gradient} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ColorScaleDetailsDrawer
        scale={selectedScale}
        onClose={() => setSelectedKey(null)}
      />
      <CreateColorScaleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
